import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Download, Image as ImageIcon } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";

const W = 1080;
const H = 1350;

const wrap = (ctx, text, maxW) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = w;
    } else line = test;
  });
  if (line) lines.push(line);
  return lines.slice(0, 2);
};

const drawPoster = async (qr, { title, subtitle, url, whatsapp }) => {
  await document.fonts?.ready;
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0B0813";
  ctx.fillRect(0, 0, W, H);
  [[150, 180, "rgba(124,58,237,0.45)"], [960, 1180, "rgba(255,107,0,0.35)"]].forEach(([x, y, col]) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, 520);
    g.addColorStop(0, col);
    g.addColorStop(1, "rgba(11,8,19,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  });
  ctx.fillStyle = "#FF6B00";
  ctx.font = "600 30px 'JetBrains Mono', monospace";
  ctx.fillText("● EUTEVILA · FOTOS DO EVENTO", 90, 130);
  ctx.fillStyle = "#F5F3FF";
  ctx.font = "800 120px Syne, sans-serif";
  ctx.fillText("Eu te vi", 84, 270);
  ctx.fillStyle = "#FF6B00";
  ctx.fillText("lá.", 84, 390);
  ctx.fillStyle = "#F5F3FF";
  ctx.font = "600 46px Outfit, sans-serif";
  ctx.fillText(subtitle, 90, 470);
  ctx.fillStyle = "#B8B0CC";
  ctx.font = "500 38px Outfit, sans-serif";
  wrap(ctx, title, W - 180).forEach((l, i) => ctx.fillText(l, 90, 528 + i * 46));
  const size = 440;
  const x = (W - size - 60) / 2;
  const y = 610;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.roundRect(x, y, size + 60, size + 60, 36);
  ctx.fill();
  ctx.drawImage(qr, x + 30, y + 30, size, size);
  ctx.strokeStyle = "#FF6B00";
  ctx.lineWidth = 8;
  [[x - 24, y - 24, 1, 1], [x + size + 84, y - 24, -1, 1], [x - 24, y + size + 84, 1, -1], [x + size + 84, y + size + 84, -1, -1]].forEach(([cx, cy, dx, dy]) => {
    ctx.beginPath();
    ctx.moveTo(cx, cy + 60 * dy);
    ctx.lineTo(cx, cy);
    ctx.lineTo(cx + 60 * dx, cy);
    ctx.stroke();
  });
  ctx.textAlign = "center";
  ctx.fillStyle = "#F5F3FF";
  let fs = 30;
  const shown = url.replace(/^https?:\/\//, "");
  do {
    ctx.font = `500 ${fs}px 'JetBrains Mono', monospace`;
    fs -= 2;
  } while (ctx.measureText(shown).width > W - 140 && fs > 16);
  ctx.fillText(ctx.measureText(shown).width > W - 140 ? new URL(url).host : shown, W / 2, 1200);
  if (whatsapp) {
    ctx.fillStyle = "#FF6B00";
    ctx.font = "600 36px Outfit, sans-serif";
    ctx.fillText(`Dúvidas? WhatsApp ${whatsapp}`, W / 2, 1270);
  }
  return c.toDataURL("image/png");
};

const save = (dataUrl, name) => {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name;
  a.click();
};

export const QrPoster = ({ path, title, subtitle = "Escaneie e baixe suas fotos", fileName = "eutevila", testid = "qr-poster" }) => {
  const ref = useRef(null);
  const { data: settings } = useFetch("/settings/public", {});
  const url = `${window.location.origin}${path}`;
  const canvas = () => ref.current?.querySelector("canvas");

  const downloadQr = () => save(canvas().toDataURL("image/png"), `${fileName}-qrcode.png`);
  const downloadPoster = async () => {
    try {
      save(await drawPoster(canvas(), { title, subtitle, url, whatsapp: settings?.whatsapp }), `${fileName}-cartaz.png`);
    } catch (e) {
      toast.error("Não foi possível gerar o cartaz");
    }
  };

  return (
    <div className="grid gap-6 rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 sm:grid-cols-[200px_1fr] sm:items-center" data-testid={testid}>
      <div ref={ref} className="w-fit rounded-2xl bg-white p-3">
        <QRCodeCanvas value={url} size={1024} level="M" marginSize={1} fgColor="#0B0813" style={{ width: 176, height: 176 }} data-testid={`${testid}-canvas`} />
      </div>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-flash">QR Code para divulgar</p>
        <p className="mt-2 font-display text-xl font-bold">{title}</p>
        <p className="mt-1 break-all text-sm text-haze" data-testid={`${testid}-url`}>{url}</p>
        <p className="mt-2 text-xs text-haze">Mostre na hora das fotos: quem escanear cai direto na galeria para baixar as fotos.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={downloadPoster} className="inline-flex items-center gap-2 rounded-full bg-flash px-4 py-2.5 text-sm font-semibold text-ink hover:bg-flash-2" data-testid={`${testid}-poster-btn`}><ImageIcon className="h-4 w-4" />Baixar cartaz</button>
          <button onClick={downloadQr} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm" data-testid={`${testid}-png-btn`}><Download className="h-4 w-4" />Só o QR (PNG)</button>
          <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copiado"); }} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm" data-testid={`${testid}-copy-btn`}><Copy className="h-4 w-4" />Copiar link</button>
        </div>
      </div>
    </div>
  );
};
